import {dropDisabledNoRendererReads} from "#material/nodes/color/brightness-contrast/validation"
import {optionalMaterialInput} from "#material/nodes/base/connection/validation/parameters"
import type {MaterialNodeDefinition} from "#material/nodes/base/material/types/definition"
import {ExchangeNodeName} from "#material/exchange/node-name"
import {materialColorOutput} from "#material/nodes/base/material/validation/io"
import {materialUiSchema} from "#material/nodes/base/material/validation/ui"
import type {MaterialNodeSockets} from "#material/types/material-node-sockets"
import {color, versionChain} from "@cm/graph-v3"
import {z} from "zod/v4-mini"

export interface BrightnessContrastSockets extends MaterialNodeSockets {
    inputs: {
        bright: "float" // found in inputs: -; found in settings: float; inferred from data: float
        color: "color" // found in inputs: float,color; found in settings: color; inferred from data: float3
        contrast: "float" // found in inputs: -; found in settings: float; inferred from data: float
    }
    settings: {
        bright: "float" // found in inputs: -; found in settings: float; inferred from data: float
        color: "color" // found in inputs: float,color; found in settings: color; inferred from data: float3
        contrast: "float" // found in inputs: -; found in settings: float; inferred from data: float
    }
    outputs: {
        color: "color"
    }
}

export const brightnessContrastDefinition = {
    exchangeFormat: {
        nodeName: ExchangeNodeName.ShaderNodeBrightContrast,
    },
    nodeClass: "BrightnessContrast",
    versionChain: versionChain([dropDisabledNoRendererReads]),
    ui: {
        label: "Bright/Contrast",
        color: "#6e5eb2",
        disableable: true,
        items: [
            {name: "color", label: "Color", valueType: "color", output: true},
            {input: true, label: "Color", name: "color", setting: true, valueType: "color"},
            {input: true, name: "bright", label: "Bright", valueType: "float", defaultValue: 0, range: {min: -1, max: 1}, setting: true},
            {
                input: true,
                valueType: "float",
                defaultValue: 0,
                label: "Contrast",
                name: "contrast",
                range: {min: -1, max: 1},
                setting: true,
            },
        ],
        support: {
            cycles: true,
            three: true,
        },
    },
    validation: {
        parameters: z.strictObject({
            inputs: z.strictObject({
                color: optionalMaterialInput,
                bright: optionalMaterialInput,
                contrast: optionalMaterialInput,
            }),
            settings: z.strictObject({
                color: z.optional(color),
                bright: z.optional(z.number()),
                contrast: z.optional(z.number()),
            }),
            ui: materialUiSchema,
        }),
        returns: z.strictObject({color: materialColorOutput}),
    },
} satisfies MaterialNodeDefinition<BrightnessContrastSockets>
